import React from "react";
import cn from "clsx";
import { useTranslation } from "react-i18next";

import ConfirmTooltip from "components/confirmTooltip/ConfirmTooltip";
import InnerInput from "components/inputs/InnerInput/InnerInput";

import styles from "./ListBonusesTab.module.scss";

export const CANCEL_REASON_MAX_LENGTH = 200;

export type BonusRowActionType = "claim" | "cancel";

interface BonusRowActionsProps {
  rowId: number;
  action: BonusRowActionType | null;
  isOpen: boolean;
  isLoading: boolean;
  cancelReason: string;
  onCancelReasonChange: (value: string) => void;
  onToggle: (open: boolean) => void;
  onConfirm: () => void;
}

const BonusRowActions: React.FC<BonusRowActionsProps> = ({
  rowId,
  action,
  isOpen,
  isLoading,
  cancelReason,
  onCancelReasonChange,
  onToggle,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const isDisabled = !action;
  const tooltipId = action
    ? `bonus-action-tooltip-${action}-${rowId}`
    : undefined;
  const isCancelAction = action === "cancel";
  const trimmedCancelReason = cancelReason.trim();
  const isCancelReasonValid =
    trimmedCancelReason.length > 0 &&
    trimmedCancelReason.length <= CANCEL_REASON_MAX_LENGTH;

  return (
    <div className={styles.actionsCell}>
      <button
        type="button"
        className={styles.actionsButton}
        disabled={isDisabled}
        data-tooltip-id={tooltipId}
        data-tooltip-place="left"
        aria-label={t("action")}
        onClick={(event) => {
          event.stopPropagation();

          if (isDisabled || isOpen) {
            return;
          }

          onToggle(true);
        }}
      >
        <span className={styles.actionsButtonIcon} aria-hidden>
          <span />
          <span />
          <span />
        </span>
      </button>

      {!isDisabled && action && (
        <ConfirmTooltip
          id={tooltipId!}
          title={t(isCancelAction ? "cancelBonus" : "claimBonus")}
          confirmText={t("yes")}
          cancelText={t("no")}
          isOpen={isOpen}
          isLoading={isLoading}
          confirmDisabled={isCancelAction && !isCancelReasonValid}
          closeOnConfirm={false}
          contentClassName={cn(
            styles.bonusActionTooltip,
            isCancelAction && styles.bonusActionTooltipWide,
          )}
          tooltipClassName={styles.bonusActionTooltipRoot}
          onToggle={onToggle}
          onConfirm={onConfirm}
          config={{
            position: "left",
            positionStrategy: "fixed",
          }}
        >
          <p className={styles.actionTooltipText}>
            {t(
              isCancelAction
                ? "cancelBonusConfirmation"
                : "claimBonusConfirmation",
            )}
          </p>

          {isCancelAction && (
            <div className={styles.cancelReasonField}>
              <InnerInput
                id={`bonus-cancel-reason-${rowId}`}
                type="textarea"
                label={t("cancellationReason")}
                value={cancelReason}
                rows={3}
                onChange={(value) =>
                  onCancelReasonChange(value.slice(0, CANCEL_REASON_MAX_LENGTH))
                }
              />
              <span className={styles.charCounter}>
                {cancelReason.length} / {CANCEL_REASON_MAX_LENGTH}
              </span>
            </div>
          )}
        </ConfirmTooltip>
      )}
    </div>
  );
};

export const getBonusRowAction = (
  availableActions?: string[],
): BonusRowActionType | null => {
  if (availableActions?.includes("claim")) {
    return "claim";
  }

  if (availableActions?.includes("cancel")) {
    return "cancel";
  }

  return null;
};

export default BonusRowActions;
